import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Share2, Download, Linkedin, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareCaseStudy — generates a 1200x630 branded share card as PNG
 * from the client's case study data, ready to attach to a LinkedIn post.
 */
const ShareCaseStudy = ({ client }) => {
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasCaseStudy =
    client && (client.before || client.after || (client.metrics && client.metrics.length > 0));

  if (!hasCaseStudy) return null;

  const clientUrl = `${window.location.origin}/clients/${client.slug}`;

  const generateBlob = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#0A0A0A",
    });
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await generateBlob();
      if (!dataUrl) throw new Error("no data");
      const link = document.createElement("a");
      link.download = `${client.slug}-case-study.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded — ready to attach to your LinkedIn post");
    } catch (e) {
      toast.error("Could not generate card. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const shareToLinkedIn = () => {
    // Opens LinkedIn share composer with the client page URL.
    // LinkedIn will auto-preview the page; user can attach the downloaded card too.
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(clientUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=680,height=740");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(clientUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  // Shorten helper: keep string under N chars, add ellipsis if longer
  const trim = (str, n) => (!str ? "" : str.length > n ? str.slice(0, n - 1).trim() + "…" : str);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="share-case-study-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#F62493] text-white px-5 py-2.5 hover:opacity-90 transition-opacity font-mono-label text-xs"
      >
        <Share2 size={14} /> Share this case study
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
          onClick={() => !busy && setOpen(false)}
          data-testid="share-modal"
        >
          <div className="min-h-full flex items-start justify-center p-4 md:p-8">
            <div
              className="relative bg-[#0A0A0A] border border-stone-800 rounded-sm max-w-4xl w-full my-auto"
              onClick={(e) => e.stopPropagation()}
            >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-stone-300 hover:text-white hover:bg-[#242424] transition-colors z-10"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="p-6 md:p-8 border-b border-stone-800">
              <h3 className="font-display text-2xl text-stone-100">Share on LinkedIn</h3>
              <p className="font-mono-label text-stone-300 mt-2">
                Download the branded card below, then attach it to your LinkedIn post for a scroll-stopping preview.
              </p>
            </div>

            {/* Preview */}
            <div className="p-6 md:p-8 bg-[#141414]">
              <div className="mx-auto overflow-hidden" style={{ maxWidth: 900, height: 472 }}>
                <div style={{ transform: "scale(0.75)", transformOrigin: "top left", width: 1200, height: 630 }}>
                  <ShareCard client={client} trim={trim} />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 md:p-8 flex flex-wrap items-center gap-3 justify-end border-t border-stone-800">
              <button
                type="button"
                onClick={copyLink}
                data-testid="copy-link-btn"
                className="inline-flex items-center gap-2 rounded-full border border-stone-800 text-stone-100 px-5 py-2.5 hover:border-[#F62493] hover:text-[#F62493] transition-colors font-mono-label text-xs"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Link copied" : "Copy client link"}
              </button>
              <button
                type="button"
                onClick={download}
                disabled={busy}
                data-testid="download-card-btn"
                className="inline-flex items-center gap-2 rounded-full border border-stone-800 text-stone-100 px-5 py-2.5 hover:border-white transition-colors font-mono-label text-xs disabled:opacity-50"
              >
                <Download size={14} /> {busy ? "Generating…" : "Download card"}
              </button>
              <button
                type="button"
                onClick={shareToLinkedIn}
                data-testid="share-linkedin-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#F62493] text-white px-5 py-2.5 hover:opacity-90 transition-opacity font-mono-label text-xs"
              >
                <Linkedin size={14} /> Open LinkedIn composer
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// The renderable share card. Fixed 1200×630 to match LinkedIn's preview aspect.
// Ref is forwarded so the parent can snapshot it with html-to-image.
const ShareCard = React.forwardRef(({ client, trim }, ref) => {
  const before = trim(client.before, 160);
  const after = trim(client.after, 160);
  const metrics = (client.metrics || []).slice(0, 4);

  return (
    <div
      ref={ref}
      style={{
        width: 1200,
        height: 630,
        transform: "scale(0.75)",
        transformOrigin: "top left",
        marginBottom: -157, // compensate for scale in modal preview
      }}
      className="relative bg-[#0A0A0A] text-white overflow-hidden"
    >
      {/* Big pink corner block */}
      <div
        className="absolute top-0 right-0 h-full"
        style={{ width: 460, backgroundColor: "#F62493" }}
      />
      {/* Cover image tucked behind pink block for texture */}
      {client.cover && (
        <img
          src={client.cover}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: 460,
            objectFit: "cover",
            opacity: 0.35,
            mixBlendMode: "luminosity",
          }}
        />
      )}

      {/* Content */}
      <div style={{ position: "relative", padding: 64, height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, letterSpacing: 2, textTransform: "uppercase", color: "#F62493" }}>
          Case Study · Christina Bellaro
        </div>

        <div style={{ marginTop: 18, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
          {client.industry || "Selected Work"}
        </div>
        <h1
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 76,
            lineHeight: 1.02,
            marginTop: 12,
            maxWidth: 700,
            color: "#F7F3ED",
            fontWeight: 500,
          }}
        >
          {client.name}
        </h1>

        {/* Case-study excerpt */}
        <div style={{ marginTop: 24, maxWidth: 680 }}>
          {before && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 6 }}>
                Before
              </div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, lineHeight: 1.35, color: "#F7F3ED" }}>
                {before}
              </div>
            </div>
          )}
          {after && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#F62493", marginBottom: 6 }}>
                After
              </div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, lineHeight: 1.35, color: "#F7F3ED" }}>
                {after}
              </div>
            </div>
          )}
        </div>

        {/* Metric pills */}
        {metrics.length > 0 && (
          <div style={{ marginTop: "auto", display: "flex", flexWrap: "wrap", gap: 10 }}>
            {metrics.map((m, i) => (
              <span
                key={i}
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  padding: "10px 20px",
                  borderRadius: 999,
                  backgroundColor: "#F62493",
                  color: "white",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        )}

        {/* Footer signature */}
        <div style={{ marginTop: metrics.length > 0 ? 24 : "auto", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: "#F7F3ED" }}>Christina Bellaro</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginTop: 4 }}>
              Sr. Graphic Designer · christinabellaro.com
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ShareCaseStudy;
